import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, RefreshCw, TrendingUp } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchPickupOpportunityPricingEvidence,
  fetchPickupPipelineData,
  type PickupOpportunityPricingEvidence
} from '@/services/pickupOpportunityService';
import type { PickupPipelineData } from '@/types/pickupOpportunity';
import { createComponentLogger } from '@/utils/logger';
import { PickupDriftInspectorSheet } from '@/components/reports/PickupDriftInspectorSheet';
import { pickupInvestorPricingContextFromOpportunity } from '@/components/reports/pickupInvestorPricing';
import { PickupDispositionSheet } from './PickupDispositionSheet';
import { PickupPipelineTable } from './PickupPipelineTable';
import {
  buildPickupPipelineRows,
  filterPickupPipelineRows,
  pickupPipelineWorkState,
  type PickupPipelineFilter
} from './pickupPipelineModel';

const log = createComponentLogger('PickupPipeline', 'secondary');
export const PICKUP_PIPELINE_HEADER_SLOT_ID = 'pickup-pipeline-workspace-header-actions';
const EMPTY_DATA: PickupPipelineData = {
  opportunities: [],
  attempts: [],
  users: [],
  cacheSchedules: []
};

interface PickupPipelineProps {
  embedded?: boolean;
  canManageActions?: boolean;
}

export function PickupPipeline({
  embedded = false,
  canManageActions = true
}: PickupPipelineProps = {}) {
  const [data, setData] = useState<PickupPipelineData>(EMPTY_DATA);
  const [filter, setFilter] = useState<PickupPipelineFilter>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [comparisonRowId, setComparisonRowId] = useState<string | null>(null);
  const [comparisonReturnFocus, setComparisonReturnFocus] = useState<HTMLButtonElement | null>(null);
  const [comparisonPricingEvidence, setComparisonPricingEvidence] = useState<PickupOpportunityPricingEvidence | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceHeaderTarget, setWorkspaceHeaderTarget] = useState<HTMLElement | null>(null);
  const deepLinkOpportunityId = useRef(
    new URLSearchParams(window.location.search).get('opportunity')
  );
  const deepLinkHandled = useRef(false);
  const initialFilterResolved = useRef(false);
  const comparisonPricingRequestId = useRef(0);

  const load = useCallback(async (background = false) => {
    if (background) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const nextData = await fetchPickupPipelineData();
      setData(nextData);
      setError(null);
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : 'Could not load the Pick Up Pipeline.';
      setError(message);
      log.error(
        'Pipeline load failed',
        { metadata: { background } },
        nextError instanceof Error ? nextError : undefined
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(true), 60_000);
    const handleWindowFocus = () => void load(true);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [load]);

  useLayoutEffect(() => {
    setWorkspaceHeaderTarget(
      embedded ? document.getElementById(PICKUP_PIPELINE_HEADER_SLOT_ID) : null
    );
  }, [embedded]);

  const rows = useMemo(() => buildPickupPipelineRows(data), [data]);
  const visibleRows = useMemo(() => filterPickupPipelineRows(rows, filter), [rows, filter]);
  const selectedRow = useMemo(
    () => rows.find((row) => row.opportunity.id === selectedId) || null,
    [rows, selectedId]
  );
  const comparisonRow = useMemo(
    () => rows.find((row) => row.opportunity.id === comparisonRowId) || null,
    [comparisonRowId, rows]
  );
  const comparisonTarget = useMemo(() => comparisonRow ? {
    encompassLoanId: comparisonRow.opportunity.encompass_loan_id,
    obLoanCacheId: comparisonRow.opportunity.ob_loan_cache_id,
    obLoanId: comparisonRow.opportunity.ob_loan_id,
    loanNumber: comparisonRow.opportunity.los_loan_number,
    borrowerName: comparisonRow.opportunity.borrower_name,
    pricing: pickupInvestorPricingContextFromOpportunity(
      comparisonRow.opportunity,
      comparisonPricingEvidence,
      comparisonRow.attempts
    )
  } : null, [comparisonPricingEvidence, comparisonRow]);
  const handleComparisonOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setComparisonRowId(null);
    }
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!deepLinkHandled.current) {
      deepLinkHandled.current = true;
      const linkedRow = rows.find(
        (row) => row.opportunity.id === deepLinkOpportunityId.current
      );
      if (linkedRow) {
        setSelectedId(linkedRow.opportunity.id);
        setFilter(pickupPipelineWorkState(linkedRow));
        return;
      }
    }
    if (!initialFilterResolved.current) {
      initialFilterResolved.current = true;
      if (rows.length > 0 && visibleRows.length === 0) {
        setFilter(pickupPipelineWorkState(rows[0]));
        return;
      }
    }
    if (visibleRows.length > 0
      && (!selectedId || !visibleRows.some((row) => row.opportunity.id === selectedId))) {
      setSelectedId(visibleRows[0].opportunity.id);
    }
  }, [loading, rows, selectedId, visibleRows]);

  useEffect(() => {
    if (selectedId && !selectedRow && !loading) {
      setSelectedId(null);
    }
  }, [loading, selectedId, selectedRow]);

  useEffect(() => {
    if (comparisonRowId && !comparisonRow && !loading) {
      setComparisonRowId(null);
    }
  }, [comparisonRow, comparisonRowId, loading]);

  useEffect(() => {
    const currentRequest = comparisonPricingRequestId.current + 1;
    comparisonPricingRequestId.current = currentRequest;
    if (!comparisonRow) {
      setComparisonPricingEvidence(null);
      return;
    }
    setComparisonPricingEvidence(null);
    void fetchPickupOpportunityPricingEvidence(comparisonRow.opportunity.latest_result_id)
      .then((evidence) => {
        if (comparisonPricingRequestId.current === currentRequest) {
          setComparisonPricingEvidence(evidence);
        }
      })
      .catch((nextError: unknown) => {
        if (comparisonPricingRequestId.current === currentRequest) {
          setComparisonPricingEvidence(null);
          log.warn('Failed to load comparison pricing evidence', {
            metadata: {
              opportunityId: comparisonRow.opportunity.id,
              message: nextError instanceof Error ? nextError.message : 'Unknown error'
            }
          });
        }
      });
  }, [comparisonRow]);

  const selectNextVisibleRow = useCallback(() => {
    if (visibleRows.length === 0) {
      return;
    }
    const selectedIndex = visibleRows.findIndex((row) => row.opportunity.id === selectedId);
    const nextIndex = selectedIndex < 0 ? 0 : (selectedIndex + 1) % visibleRows.length;
    setSelectedId(visibleRows[nextIndex].opportunity.id);
  }, [selectedId, visibleRows]);

  const refreshButton = (
    <Button variant="outline" size="xs" className="whitespace-nowrap" onClick={() => void load(true)} disabled={refreshing}>
      {refreshing ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
      Refresh pipeline
    </Button>
  );

  return (
    <div
      data-pickup-pipeline-workspace
      className={embedded
        ? 'flex h-full min-h-0 flex-col overflow-hidden bg-card [contain:strict]'
        : 'flex min-h-screen flex-col bg-background p-4 md:p-6'}
    >
      {embedded && workspaceHeaderTarget && createPortal(refreshButton, workspaceHeaderTarget)}
      {!embedded && (
      <header className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        {!embedded && (
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary" aria-hidden="true" />
              <h1 className="text-2xl font-bold md:text-3xl">Pick Up Pipeline</h1>
            </div>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              Secondary-desk queue for renegotiations, investor switches, and claimed capture. Pricing runs daily at 18:05 UTC (11:05 AM PDT; 10:05 AM PST).
            </p>
          </div>
        )}
        {refreshButton}
      </header>
      )}

      {error && (
        <Alert variant="destructive" className="shrink-0 rounded-none border-x-0 border-t-0">
          <AlertTitle>Pipeline unavailable</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <Button variant="outline" size="xs" onClick={() => void load()}>Try again</Button>
          </AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid h-0 min-h-0 flex-1 grid-cols-[356px_minmax(0,1fr)] border-y bg-card" aria-label="Loading Pick Up Pipeline">
          <Skeleton className="h-full rounded-none border-r" />
          <Skeleton className="h-full rounded-none" />
        </div>
      ) : (
          <div
            data-pickup-pipeline-frame
            className="grid h-0 min-h-0 min-w-[1180px] flex-1 grid-cols-[356px_minmax(0,1fr)] border-y bg-card"
          >
            <PickupPipelineTable
              rows={visibleRows}
              allRows={rows}
              filter={filter}
              selectedId={selectedId}
              onFilterChange={setFilter}
              onSelect={(row) => setSelectedId(row.opportunity.id)}
              onInspectVerification={(row, element) => {
                setComparisonReturnFocus(element);
                setComparisonRowId(row.opportunity.id);
              }}
            />
            <PickupDispositionSheet
              row={selectedRow}
              users={data.users}
              open={Boolean(selectedRow)}
              canManageActions={canManageActions}
              onUpdated={() => load(true)}
              onSelectNext={selectNextVisibleRow}
            />
          </div>
      )}
      <PickupDriftInspectorSheet
        target={comparisonTarget}
        open={Boolean(comparisonRow)}
        returnFocusElement={comparisonReturnFocus}
        onOpenChange={handleComparisonOpenChange}
      />
    </div>
  );
}

export default PickupPipeline;
